"use client";

import { ArrowOutward } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Image from "next/image";
import { useState } from "react";
import { SLHtmlFromLangFile } from "../sections/home/SLHtmlFromLangFile";

interface SLCardWithImage {
  title: string;
  subtitle: string;
  description: string | React.ReactNode;
  imageUrl: string;
  imageAlt?: string;
  url: string;
  nofollow?: boolean;
}

export const SLCardWithImage = ({
  title,
  subtitle,
  description,
  imageUrl,
  imageAlt,
  url,
  nofollow = false,
}: SLCardWithImage) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("xs"), { noSsr: true });
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  return (
    <Box sx={{ px: isXs ? 1 : 0 }}>
      <Card
        sx={{
          display: "flex",
          position: "relative",
          height: "100%",
          flexDirection: "column",
          textDecoration: "none",
          backgroundColor: theme.palette.background.paper,
          boxShadow: "none",

          borderRadius: "0",
        }}
      >
        <Box
          sx={{
            height: "300px",
            minHeight: imageUrl ? "300px" : "100px",
            maxHeight: "300px",
            position: "relative",
            width: "100%",
            overflow: "hidden",
          }}
        >
          {imageUrl && (
            <>
              {/* Loading skeleton */}
              {imageLoading && (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  animation="wave"
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1,
                  }}
                />
              )}

              {/* Error fallback */}
              {imageError && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: theme.palette.grey[200],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.palette.text.secondary,
                    fontSize: "0.875rem",
                  }}
                >
                  Foto nicht verfügbar
                </Box>
              )}

              {/* Optimized image */}
              {!imageError && (
                <Image
                  src={imageUrl}
                  alt={imageAlt || title}
                  fill
                  style={{
                    objectFit: imageUrl.includes("svg") ? "fill" : "cover",
                    marginLeft: imageUrl.includes("svg") ? "20px" : "0",
                    transition: "opacity 0.3s ease-in-out",
                    opacity: imageLoading ? 0 : 1,
                  }}
                  onLoad={() => setImageLoading(false)}
                  onError={() => {
                    setImageLoading(false);
                    setImageError(true);
                  }}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              )}
            </>
          )}
        </Box>

        <CardContent
          sx={{
            flexGrow: 1,
            height: "stretch",
          }}
        >
          <Stack
            sx={{
              justifyContent: "space-between",
              height: "stretch",
              gap: 3,
              py: 1,
            }}
          >
            <Stack width="100%" alignItems="flex-start">
              <Stack
                direction="row"
                justifyContent="space-between"
                width="100%"
              >
                {/* Title */}
                <Typography
                  variant="h5"
                  component="h3"
                  gutterBottom
                  sx={{
                    color: theme.palette.text.primary,
                  }}
                >
                  {title}
                </Typography>
                <Box>
                  <Tooltip title="Zur Gerichtsseite">
                    <IconButton
                      href={url}
                      target={nofollow ? "_self" : "_blank"}
                      rel={nofollow ? "nofollow" : "noreferrer"}
                      aria-label={`Website von ${title} besuchen`}
                    >
                      <ArrowOutward
                        sx={{ color: theme.palette.text.secondary }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>

              <Typography
                variant="body1"
                gutterBottom
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                Sitz: {subtitle}
              </Typography>

              <Stack spacing={1} gap={3} sx={{ flexDirection: "column" }}>
                {/* Summary */}
                <Typography
                  variant="body1"
                  color="text.primary"
                  sx={{
                    mb: 3,
                    flexGrow: 1,
                    display: "-webkit-box",

                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.5,
                  }}
                >
                  {typeof description === "string" ? (
                    <SLHtmlFromLangFile htmlString={description} />
                  ) : (
                    description
                  )}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
